import { useState, useCallback, useEffect, useRef } from 'preact/hooks'
import { useSearch } from 'wouter-preact'
import { api } from '../lib/api.js'
import { intervallText, datumText } from '../lib/format.js'
import { istWischenNachLinks } from '../lib/gesture.js'
import { Kartentext } from '../components/Kartentext.jsx'
import { McKarte } from '../components/McKarte.jsx'
import { BildKarte } from '../components/BildKarte.jsx'
import { Fortschrittslinie } from '../components/Fortschrittslinie.jsx'
import { XpBlitz } from '../components/XpBlitz.jsx'
import { Laden, Fehler } from '../components/Zustand.jsx'
import { Zusammenfassung } from './Zusammenfassung.jsx'
import { setzeAbzeichen } from '../lib/abzeichen.js'

/**
 * Der Lernbildschirm. Auf dem Bildschirm sind genau fünf Dinge (DESIGN.md):
 * die Karte, die Fortschrittslinie, die vier Bewertungsknöpfe, ein dezentes
 * Zurück und ein leises Überspringen. Kein Themenname, kein XP-Zähler, kein Streak, keine Seitenleiste.
 *
 * Die Fälligkeitsschlange kommt einmal vom Server (`due_queue`) und lebt danach
 * hier — dadurch kostet Bewerten genau eine Anfrage und Überspringen keine.
 */

const BEWERTUNGEN = [
  { rating: 'again', label: 'Wieder' },
  { rating: 'hard', label: 'Schwer' },
  { rating: 'good', label: 'Gut' },
  { rating: 'easy', label: 'Einfach' },
]

// Tasten 1–4 bewerten in der Reihenfolge der Knöpfe, 0 schiebt ans Ende.
const TASTE_ZU_BEWERTUNG = { 1: 'again', 2: 'hard', 3: 'good', 4: 'easy' }

const WISCH_HINWEIS_SCHLUESSEL = 'karteikarten:wisch-gesehen'

/**
 * Karte ans Ende der Schlange, ohne den Lernstand anzufassen — dasselbe wie
 * Wischen nach links und Taste 0, nur sichtbar. Leise gesetzt: es ist die
 * Ausweichtür neben den vier Bewertungen, nicht eine fünfte.
 *
 * Bei nur einer Karte gäbe es kein „später“; der Platz bleibt dann trotzdem
 * reserviert, damit die Bewertungsknöpfe nicht die Höhe wechseln.
 */
function Ueberspringen({ sichtbar, aufKlick }) {
  if (!sichtbar) return <div class="lernen__ueberspringen" aria-hidden="true" />
  return (
    <button type="button" class="lernen__ueberspringen meta" onClick={aufKlick}>
      Überspringen
    </button>
  )
}

export function Lernen() {
  const suche = useSearch()
  const prefix = new URLSearchParams(suche).get('prefix') || ''

  const [schlange, setSchlange] = useState([])
  const [zeiger, setZeiger] = useState(0)
  const [nutzlast, setNutzlast] = useState(null)
  const [naechsterTermin, setNaechsterTermin] = useState(null)
  const [umgedreht, setUmgedreht] = useState(false)
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState(null)
  const [erledigt, setErledigt] = useState(0)
  const [proBewertung, setProBewertung] = useState({ again: 0, hard: 0, good: 0, easy: 0 })
  const [sitzungsXp, setSitzungsXp] = useState(0)
  const [stand, setStand] = useState(null)
  const [blitz, setBlitz] = useState(null)
  const [wischHinweis, setWischHinweis] = useState(
    () => !localStorage.getItem(WISCH_HINWEIS_SCHLUESSEL),
  )
  const beruehrung = useRef(null)

  const start = useCallback(() => {
    setLaedt(true)
    setFehler(null)
    api
      .get('/api/study/next', { prefix })
      .then((daten) => {
        setSchlange(daten.due_queue ?? [])
        setZeiger(0)
        setNutzlast(daten.card ? daten : null)
        setNaechsterTermin(daten.card ? null : (daten.next_due ?? null))
        setUmgedreht(false)
        setErledigt(0)
        setLaedt(false)
      })
      .catch((f) => {
        setFehler(f)
        setLaedt(false)
      })
  }, [prefix])

  useEffect(() => {
    start()
  }, [start])

  /** Lädt die Karte an einer Stelle der Schlange nach. */
  const ladeKarte = useCallback(
    (liste, index) => {
      const id = liste[index]
      if (!id) {
        setNutzlast(null)
        return
      }
      setUmgedreht(false)
      api
        .get('/api/study/card', { prefix, id })
        .then((daten) => setNutzlast(daten))
        .catch((f) => setFehler(f))
    },
    [prefix],
  )

  /**
   * Bewertung senden und zur nächsten Karte. MC-Karten kommen hier ohne
   * Umdrehen an — dort ergibt sich die Bewertung aus der Auswahl.
   */
  const sendeBewertung = useCallback(
    (rating) => {
      if (!nutzlast?.card) return
      const id = nutzlast.card.id
      api
        .post('/api/study/review', { id, rating })
        .then((ergebnis) => {
          const g = ergebnis?.gamification
          if (g) {
            setStand(g)
            setSitzungsXp((x) => x + (g.xp_gained || 0))
          }
          setProBewertung((v) => ({ ...v, [rating]: (v[rating] || 0) + 1 }))
          if (g?.xp_gained) {
            // Der Blitz lebt außerhalb der Knopfreihe: die wird beim
            // Kartenwechsel unmontiert, die Rückmeldung soll aber ihre 600ms
            // an der Stelle des gedrückten Knopfes stehen bleiben.
            setBlitz({ rating, betrag: g.xp_gained })
            setTimeout(() => setBlitz(null), 600)
          }
          setErledigt((n) => n + 1)
          const rest = schlange.filter((k) => k !== id)
          setSchlange(rest)
          const naechster = zeiger >= rest.length ? 0 : zeiger
          setZeiger(naechster)
          if (!rest.length) setNutzlast(null)
          else ladeKarte(rest, naechster)
        })
        .catch((f) => setFehler(f))
    },
    [nutzlast, schlange, zeiger, ladeKarte],
  )

  /** Bewerten über die vier Knöpfe — erst nach dem Umdrehen. */
  const bewerten = useCallback(
    (rating) => {
      if (!umgedreht) return
      sendeBewertung(rating)
    },
    [umgedreht, sendeBewertung],
  )

  /**
   * Karte ans Ende der Schlange. Kein Serveraufruf, kein Lernstand ändert sich —
   * identisch zum bisherigen „Überspringen" der YouTube-App.
   */
  const spaeter = useCallback(() => {
    if (!nutzlast?.card || schlange.length < 2) return
    const id = nutzlast.card.id
    const rest = [...schlange.filter((k) => k !== id), id]
    setSchlange(rest)
    setZeiger(0)
    if (wischHinweis) {
      localStorage.setItem(WISCH_HINWEIS_SCHLUESSEL, '1')
      setWischHinweis(false)
    }
    ladeKarte(rest, 0)
  }, [nutzlast, schlange, wischHinweis, ladeKarte])

  // Der Listener wird einmal registriert und liest die Aktionen über eine Ref.
  // Würde er direkt über bewerten/spaeter schließen, wäre er immer nur so
  // frisch wie der zuletzt ausgeführte Effekt — ein Tastendruck unmittelbar
  // nach dem Laden liefe dann auf einen veralteten Zustand.
  const aktionen = useRef({ bewerten, spaeter })
  aktionen.current = { bewerten, spaeter }

  // Tastatur: Leertaste/Enter dreht um, 1–4 bewerten, 0 schiebt ans Ende.
  useEffect(() => {
    function beiTaste(ereignis) {
      const ziel = ereignis.target
      if (ziel instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(ziel.tagName)) return

      if (ereignis.key === ' ' || ereignis.key === 'Enter') {
        ereignis.preventDefault()
        setUmgedreht(true)
        return
      }
      if (ereignis.key === '0') {
        ereignis.preventDefault()
        aktionen.current.spaeter()
        return
      }
      const rating = TASTE_ZU_BEWERTUNG[ereignis.key]
      if (rating) {
        ereignis.preventDefault()
        aktionen.current.bewerten(rating)
      }
    }

    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [])

  function beiBeruehrungStart(ereignis) {
    const punkt = ereignis.touches?.[0]
    // Mehr als ein Finger ist nie ein Wischen. Ohne diese Bedingung hat ein
    // Auf- oder Zuziehen die Karte nach hinten gelegt.
    if (!punkt || ereignis.touches.length > 1) {
      beruehrung.current = null
      return
    }
    beruehrung.current = { x: punkt.clientX, y: punkt.clientY, t: performance.now() }
  }

  function beiBeruehrungEnde(ereignis) {
    const start = beruehrung.current
    const punkt = ereignis.changedTouches?.[0]
    beruehrung.current = null
    if (!start || !punkt) return
    // Zwischendurch kam ein zweiter Finger dazu: dann war es eine Zoomgeste.
    if (ereignis.changedTouches.length > 1 || ereignis.touches?.length) return
    const bewegung = {
      dx: punkt.clientX - start.x,
      dy: punkt.clientY - start.y,
      dauer: performance.now() - start.t,
    }
    if (istWischenNachLinks(bewegung)) spaeter()
  }

  const fertig = !nutzlast?.card

  /**
   * Am Ende einer Sitzung die Zahl am App-Symbol nachziehen.
   *
   * Hier und nicht nach jeder Bewertung: der Lernbildschirm kennt nur sein
   * eigenes Thema, die Gesamtzahl steht in /api/topics. Einmal am Schluss
   * kostet eine Anfrage, nach jeder Karte wären es Dutzende — und genau der
   * Schluss ist der Moment, an dem man die App zuklappt.
   */
  useEffect(() => {
    if (!fertig || !erledigt) return
    api
      .get('/api/topics', {})
      .then((daten) => setzeAbzeichen(daten.due_total))
      .catch(() => {
        // Ein nicht nachgezogenes Abzeichen ist kein Grund für eine Meldung.
      })
  }, [fertig, erledigt])

  if (laedt) return <Laden />
  if (fehler) return <Fehler fehler={fehler} erneut={start} />

  const istMc = nutzlast?.card?.card_type === 'mc' && Array.isArray(nutzlast.card.options)
  const istIo = nutzlast?.card?.card_type === 'io'

  return (
    <main class="lernen">
      <Fortschrittslinie erledigt={erledigt} gesamt={erledigt + schlange.length} />

      <a class="lernen__zurueck" href="/" aria-label="Zurück zur Themenliste">
        ←
      </a>

      {fertig ? (
        erledigt > 0 ? (
          <Zusammenfassung
            sitzung={{ bewertet: erledigt, xp: sitzungsXp, proBewertung }}
            stand={stand}
            restFaellig={0}
          />
        ) : (
          <div class="lernen__fertig">
            <p>Hier ist gerade nichts fällig.</p>
            {naechsterTermin ? (
              <p class="meta">Nächste Karte ab {datumText(naechsterTermin)}</p>
            ) : null}
            <a class="knopf knopf--still" href="/">
              Zur Themenliste
            </a>
          </div>
        )
      ) : istMc ? (
        <>
        <div
          class="lernen__karte"
          onTouchStart={beiBeruehrungStart}
          onTouchEnd={beiBeruehrungEnde}
        >
          <McKarte
            // Ohne key behielte die nächste MC-Karte die Auswahl der vorigen.
            key={nutzlast.card.id}
            karte={nutzlast.card}
            aufWeiter={sendeBewertung}
          />
        </div>
        <div class="lernen__fuss">
          <Ueberspringen sichtbar={schlange.length > 1} aufKlick={spaeter} />
        </div>
        </>
      ) : (
        <>
          <div
            class="lernen__karte"
            role="button"
            tabIndex={0}
            aria-label="Karte umdrehen"
            onClick={() => setUmgedreht(true)}
            onTouchStart={beiBeruehrungStart}
            onTouchEnd={beiBeruehrungEnde}
          >
            {istIo ? (
              <BildKarte karte={nutzlast.card} umgedreht={umgedreht} />
            ) : (
              <Kartentext karte={nutzlast.card} umgedreht={umgedreht} />
            )}

            {/* Verschwindet dauerhaft, sobald einmal gewischt wurde — danach
                hat der Bildschirm die fünf Dinge aus DESIGN.md und sonst nichts. */}
            {wischHinweis && schlange.length > 1 ? (
              <p class="lernen__wischhinweis meta">← wischen = überspringen</p>
            ) : null}
          </div>

          {/* Der Fuß steht vor und nach dem Umdrehen gleich hoch: die
              Bewertungen erscheinen in einer schon reservierten Zeile, und
              „Überspringen“ bleibt dabei, wo der Daumen es gefunden hat. */}
          <div class="lernen__fuss">
            <div class="lernen__bewertungen">
              {umgedreht
                ? BEWERTUNGEN.map(({ rating, label }) => (
                    <button
                      key={rating}
                      type="button"
                      class="knopf knopf--bewertung"
                      onClick={() => bewerten(rating)}
                    >
                      <span class="knopf__label">{label}</span>
                      <span class="knopf__intervall zahl">
                        {intervallText(nutzlast.rating_previews?.[rating]?.interval)}
                      </span>
                    </button>
                  ))
                : null}
            </div>
            <Ueberspringen sichtbar={schlange.length > 1} aufKlick={spaeter} />
          </div>
        </>
      )}

      {/* Außerhalb der Zweige: die Rückmeldung gilt für normale und
          MC-Karten, und sie überlebt den Wechsel zur nächsten Karte. */}
      {blitz ? (
        <div
          class={`lernen__blitzzone ${istMc ? 'lernen__blitzzone--mitte' : ''}`}
          style={{ '--spalte': BEWERTUNGEN.findIndex((b) => b.rating === blitz.rating) + 1 }}
        >
          <XpBlitz betrag={blitz.betrag} />
        </div>
      ) : null}
    </main>
  )
}
